// Préparation des visuels envoyés au bucket `signature-assets` (logos, bannières).
//
// Deux chemins, volontairement séparés :
// - GIF de logo : le fichier est stocké TEL QUEL (aucune transformation, l'animation serait
//   détruite), sous un nom dérivé du contenu (`logos/<brand_id>-<hash8>.gif`) pour que tout
//   remplacement change l'URL et invalide les caches des destinataires. Les dimensions sont lues
//   dans l'en-tête GIF ; seuls les attributs d'AFFICHAGE (logo_width/logo_height) sont réduits.
// - tout le reste (PNG, JPG, bannières) : chemin existant, nom horodaté, fichier inchangé.

/** Hauteur d'affichage maximale d'un logo animé, en px ; le fichier n'est jamais redimensionné. */
import { BANNER_WARN_BYTES } from './types.ts';

export const LOGO_MAX_DISPLAY_HEIGHT = 78;
/** Au-delà : avertissement (l'image est retéléchargée à chaque ouverture de mail). */
export const LOGO_GIF_WARN_BYTES = 500_000;
/** Au-delà : refus. */
export const LOGO_GIF_MAX_BYTES = 2_000_000;
/** Limite existante pour les autres visuels (PNG/JPG, bannières). */
export const ASSET_MAX_BYTES = 1_000_000;
/**
 * Valeur passée à storage-js, qui la préfixe par `max-age=` : l'objet ressort avec
 * `Cache-Control: max-age=31536000, public, immutable`. Sûr : chaque URL est unique
 * (hash du contenu pour les GIF, horodatage pour le reste), un remplacement change l'URL.
 */
export const ASSET_CACHE_CONTROL = '31536000, public, immutable';

export type AssetFolder = 'logos' | 'banners';

export interface GifLogoPlan {
  kind: 'gif';
  path: string;
  bytes: Uint8Array; // la référence d'origine, jamais copiée ni modifiée
  contentType: 'image/gif';
  width: number; // dimensions du fichier
  height: number;
  logo_width: number; // dimensions d'affichage (hauteur ≤ LOGO_MAX_DISPLAY_HEIGHT, ratio conservé)
  logo_height: number;
  hash: string; // sha-256 hexadécimal complet
}
export interface RasterPlan {
  kind: 'raster';
  path: string;
  bytes: Uint8Array;
  contentType: string;
  /** Chemin déterministe (ex. icône teintée) : renvoyer le même fichier écrase sans erreur. */
  upsert?: boolean;
}
export type AssetPlan = GifLogoPlan | RasterPlan;

const GIF_SIGNATURES = ['GIF87a', 'GIF89a'];

/** Reconnaissance par l'en-tête, pas par l'extension ni le type annoncé par le navigateur. */
export function isGif(bytes: Uint8Array): boolean {
  if (bytes.length < 10) return false;
  const sig = String.fromCharCode(...bytes.subarray(0, 6));
  return GIF_SIGNATURES.includes(sig);
}

/** Largeur et hauteur de l'écran logique : octets 6..9 de l'en-tête, 16 bits petit-boutiste. */
export function gifDimensions(bytes: Uint8Array): { width: number; height: number } | null {
  if (!isGif(bytes)) return null;
  const width = bytes[6] | (bytes[7] << 8);
  const height = bytes[8] | (bytes[9] << 8);
  if (!width || !height) return null;
  return { width, height };
}

/** Taille d'affichage : hauteur plafonnée, ratio conservé, entiers. Un fichier plus petit garde sa taille. */
export function logoDisplaySize(
  width: number,
  height: number,
  maxHeight = LOGO_MAX_DISPLAY_HEIGHT,
): { logo_width: number; logo_height: number } {
  const h = Math.min(height, maxHeight);
  return { logo_width: Math.max(1, Math.round((width * h) / height)), logo_height: h };
}

export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', bytes as BufferSource);
  return Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, '0')).join('');
}

const safeSegment = (s: string): string => s.toLowerCase().replace(/[^a-z0-9.-]+/g, '-').replace(/^-+|-+$/g, '') || 'x';

/** `logos/<brand_id>-<hash8>.gif` — le nom change avec le contenu, jamais avec la date. */
export function gifLogoPath(brandId: string, hash: string): string {
  return `logos/${safeSegment(brandId)}-${hash.slice(0, 8)}.gif`;
}

/** Nom horodaté du chemin existant (PNG, JPG, bannières). */
export function rasterPath(folder: AssetFolder, fileName: string, now: number): string {
  return `${folder}/${now}-${safeSegment(fileName)}`;
}

export interface AssetInput {
  bytes: Uint8Array;
  type: string; // type MIME annoncé par le navigateur
  name: string; // nom du fichier d'origine
  folder: AssetFolder;
  brandId?: string; // requis pour nommer un GIF de logo
  now?: number; // injectable pour les tests
}

/**
 * Décide du chemin de stockage et des attributs d'affichage, sans rien transformer.
 * Un GIF de logo (reconnu à l'en-tête) suit le chemin dédié ; tout le reste suit le chemin existant.
 */
export async function planAsset(input: AssetInput): Promise<AssetPlan> {
  const { bytes, folder } = input;
  if (folder === 'logos' && isGif(bytes)) {
    const dims = gifDimensions(bytes);
    if (!dims) throw new Error('GIF illisible : dimensions absentes de l’en-tête.');
    const hash = await sha256Hex(bytes);
    return {
      kind: 'gif',
      path: gifLogoPath(input.brandId ?? 'charte', hash),
      bytes,
      contentType: 'image/gif',
      ...dims,
      ...logoDisplaySize(dims.width, dims.height),
      hash,
    };
  }
  return {
    kind: 'raster',
    path: rasterPath(folder, input.name, input.now ?? Date.now()),
    bytes,
    contentType: input.type || 'application/octet-stream',
  };
}

/** « 312 Ko », « 1,4 Mo », « 2 Mo ». */
export function formatBytes(n: number): string {
  if (n < 1000) return `${n} o`;
  if (n < 1_000_000) return `${Math.round(n / 1000)} Ko`;
  return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '').replace('.', ',')} Mo`;
}

export const GIF_FIRST_FRAME_NOTE =
  'Outlook 2016 et antérieurs n’affichent que la première image d’un GIF animé : la première image doit être lisible seule (contrainte à donner au graphiste, pas un défaut).';

/** Garde-fous de taille : `error` bloque l'envoi, `warning` est affiché mais laisse passer. */
export function checkAssetSize(plan: AssetPlan, size: number): { error?: string; warning?: string } {
  if (plan.kind === 'gif') {
    if (size > LOGO_GIF_MAX_BYTES) {
      return {
        error: `GIF de ${formatBytes(size)} : refusé au-delà de ${formatBytes(LOGO_GIF_MAX_BYTES)}. Réduisez le nombre d’images ou la taille du visuel.`,
      };
    }
    if (size > LOGO_GIF_WARN_BYTES) {
      return {
        warning: `GIF de ${formatBytes(size)} : au-delà de ${
          formatBytes(LOGO_GIF_WARN_BYTES)
        }, l’image est retéléchargée à chaque ouverture de mail, et le quota de bande passante de l’hébergeur est un plafond dur.`,
      };
    }
    return {};
  }
  if (plan.path.startsWith('banners/')) {
    if (size > LOGO_GIF_MAX_BYTES) return { error: `Bannière de ${formatBytes(size)} : refusée au-delà de ${formatBytes(LOGO_GIF_MAX_BYTES)}.` };
    if (size > BANNER_WARN_BYTES) {
      return { warning: `Bannière de ${formatBytes(size)} : au-delà de ${formatBytes(BANNER_WARN_BYTES)}, l’image est retéléchargée à chaque ouverture de mail. Visez 1 200 px de large, hauteur libre.` };
    }
    return {};
  }
  if (size > ASSET_MAX_BYTES) return { error: 'Image > 1 Mo : compressez-la (les mails lourds sont pénalisés).' };
  return {};
}

/** Types de fichiers acceptés au téléversement (logos ET bannières) : PNG, JPG, GIF — animé compris, stocké tel quel. */
export const ALLOWED_IMAGE_TYPES: readonly string[] = ['image/png', 'image/jpeg', 'image/gif'];

/** Refus de type AVANT tout envoi, avec le type reçu et la liste acceptée. null = accepté. */
export function checkAssetType(type: string, name: string, bytes: Uint8Array): string | null {
  const t = (type || '').toLowerCase();
  if (isGif(bytes) || ALLOWED_IMAGE_TYPES.includes(t)) return null;
  const ext = name.includes('.') ? name.slice(name.lastIndexOf('.') + 1).toUpperCase() : 'sans extension';
  return `Type de fichier refusé : ${t || ext} — acceptés : PNG, JPG, GIF (animé compris).`;
}

/**
 * Message lisible pour une erreur renvoyée par le stockage : dit lequel des refus s'applique et
 * quelle est la limite, au lieu du texte brut du serveur. Un refus de droits est nommé comme tel.
 */
export function explainStorageError(message: string, size?: number): string {
  const m = message.toLowerCase();
  if (m.includes('row-level security') || m.includes('row level security') || m.includes('violates')) {
    return `Le stockage a refusé l’écriture (règle d’accès du bucket, pas un problème de fichier). Vérifiez que votre compte est administrateur et que les règles SELECT / INSERT / UPDATE du bucket signature-assets sont en place. Détail : ${message}`;
  }
  if (m.includes('payload too large') || m.includes('exceeded the maximum allowed size') || m.includes('413') || m.includes('too large')) {
    return `Fichier trop lourd pour le stockage${size ? ` (${formatBytes(size)})` : ''} : la limite globale du projet Supabase (Storage › Settings › « Upload file size limit ») est dépassée. Détail : ${message}`;
  }
  if (m.includes('mime') || m.includes('content-type') || m.includes('not supported')) {
    return `Type de fichier refusé par le stockage. Acceptés : PNG, JPG, GIF. Détail : ${message}`;
  }
  if (m.includes('duplicate') || m.includes('already exists')) {
    return `Un fichier du même nom existe déjà dans le stockage. Détail : ${message}`;
  }
  return `Téléversement refusé : ${message}`;
}
